import { memo } from "react";

import { CircularLoader } from "@similarweb/ui-components";

import Chart from "pro-features/components/Chart/src/Chart";
import { translate } from "app/filters/i18nFilter";
import { getColumns } from "app/pages/activation/components/Widget/config";
import { TOTAL_SCORE_CHANGE_VALUE } from "app/pages/activation/components/Widget/constants";
import { sharedAutomationKeys } from "app/pages/activation/components/Widget/dataAutomationKeys";
import { TableLoadingState } from "app/pages/activation/components/Widget/TableLoadingState/TableLoadingState";
import { textKeys } from "app/pages/activation/components/Widget/texts";
import { loaderOptions } from "app/pages/activation/configs";
import { usePieChart, useTrafficShareTableData } from "app/common/hooks/benchmark";
import type { TrafficShareMetricSchema } from "app/common/benchmark/types";
import DemandSizeChange from "app/pages/market-research/features/search-trends-page/demand-size/DemandSizeChange";
import { ITotalScoreTypes } from "app/pages/market-research/features/search-trends-page/demand-size/types";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { BenchmarkGranularity } from "../../../types/common";

import * as SC from "./styles";

type BodySchema = {
    primaryDomain: string;
    trafficShare: TrafficShareMetricSchema;
    handleHover: (isHovered: boolean) => void;
    onClick: () => void;
    isLoading?: boolean;
};

export const Body = memo(({ primaryDomain, trafficShare, handleHover, onClick, isLoading }: BodySchema) => {
    const YOY = trafficShare?.primaryData.yearOverYearShareDifference;
    const POP = trafficShare?.primaryData.periodOverPeriodShareDifference;

    const { timeGranularity } = proNavigator.getParams();

    const isSortedData = false;

    const granularityLabel =
        timeGranularity === BenchmarkGranularity.MONTHLY
            ? translate(textKeys.momChange)
            : translate(textKeys.wowChange);

    const tableData = useTrafficShareTableData({
        primary: primaryDomain,
        trafficShareData: trafficShare,
        sorted: isSortedData,
    });

    const { chartData, chartConfig } = usePieChart({
        primary: primaryDomain,
        trafficShareData: trafficShare,
        sorted: isSortedData,
    });

    const getTotalScore = (change: number) => {
        if (change > TOTAL_SCORE_CHANGE_VALUE) {
            return ITotalScoreTypes.GROWING;
        } else if (change < -TOTAL_SCORE_CHANGE_VALUE) {
            return ITotalScoreTypes.DECLINING;
        } else {
            return ITotalScoreTypes.STAGNATING;
        }
    };

    return (
        <SC.Wrapper
            onMouseEnter={() => handleHover(true)}
            onMouseLeave={() => handleHover(false)}
            onClick={onClick}
        >
            <SC.LeftContent>
                <SC.ChartContent>
                    <SC.ChartWrapper data-automation={sharedAutomationKeys.chartContainer}>
                        {isLoading ? (
                            <SC.LoaderContainer data-automation={sharedAutomationKeys.chartLoader}>
                                <CircularLoader options={loaderOptions} />
                            </SC.LoaderContainer>
                        ) : (
                            <Chart
                                data={chartData}
                                config={chartConfig}
                            />
                        )}
                    </SC.ChartWrapper>

                    <SC.TableWrapper data-automation={sharedAutomationKeys.tableContainer}>
                        {isLoading ? (
                            <TableLoadingState />
                        ) : (
                            <SC.Table
                                tableColumns={getColumns(timeGranularity)}
                                tableData={{ Data: tableData }}
                                disableRowHover
                                type="regular"
                            />
                        )}
                    </SC.TableWrapper>
                </SC.ChartContent>
            </SC.LeftContent>

            <SC.MetricsContainer data-automation={sharedAutomationKeys.changeContainer}>
                <DemandSizeChange
                    isLoading={isLoading}
                    loaderTopGap="0"
                    rawData={{ Volume: { Change: POP }, TotalScore: getTotalScore(POP) }}
                    customLabel={granularityLabel}
                    className="changeWrapper"
                    loaderClassName="changeWrapperLoader"
                    tooltip="activation.home.widget.change.tooltip.text"
                    isPP
                />

                <DemandSizeChange
                    isLoading={isLoading}
                    loaderTopGap="0"
                    rawData={{ Volume: { Change: YOY }, TotalScore: getTotalScore(YOY) }}
                    customLabel={translate(textKeys.yoyChange)}
                    className="changeWrapper"
                    loaderClassName="changeWrapperLoader"
                    tooltip="activation.home.widget.change.tooltip.text"
                    isPP
                />
            </SC.MetricsContainer>
        </SC.Wrapper>
    );
});

Body.displayName = "Body";
